import React from "react";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import { ReactionBarSelector } from "@charkour/react-reactions";
import RepliesTo from "../../../sharedComponent/RepliesTo";
import MensionUsers from "../../../sharedComponent/MensionUsers";
import ChatMessage from "../../../sharedComponent/ChatMessage";
import ChatImage from "../../chatImage";
import { emojiReactions } from "../../../common";
import ApiConfig, { BASE_URL_CHAT_Image } from "../../../api/ApiConfig";
import double_tick from "../../../assets/images/chat/double_tick.svg";
import gray_tick from "../../../assets/images/chat/gray_tick.svg";
import hr_three_dot from "../../../assets/images/hr_tree_dot.svg";
import fileIcon from "../../../assets/images/fileIcon.svg";
import downloadIcon from "../../../assets/images/ic_download.svg";
import { Button, Textarea } from "../../../components/ui";

// Extract a human filename + extension from a presigned/remote URL.
// Strips query/hash, takes the trailing path segment, decodes percent
// escapes, and surfaces the dotted extension as an UPPERCASE label so
// the file card can render a WhatsApp-style "PDF • 23 KB" detail line.
// Falls back to "Document" / "FILE" when the URL is opaque.
const getFileMeta = (url) => {
  if (!url || typeof url !== "string") {
    return { name: "Document", ext: "FILE" };
  }
  const cleanPath = url.split("?")[0].split("#")[0];
  let name = cleanPath.split("/").pop() || "Document";
  try {
    name = decodeURIComponent(name);
  } catch (_) {
    // leave the raw segment if decoding fails — better than throwing
  }
  const dotIdx = name.lastIndexOf(".");
  const ext = dotIdx >= 0 ? name.slice(dotIdx + 1).toUpperCase() : "FILE";
  return { name, ext };
};

// Date pill label for the centered divider — anchors recent messages
// against "Today" / "Yesterday" so users orient quickly without scanning timestamps.
const getDateLabel = (date) => {
  const m = moment(date);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  if (m.isSame(moment(), "year")) return m.format("MMM D");
  return m.format("MMM D, YYYY");
};

const MessageList = ({
  conversations,
  newMessages,
  userData,
  socket,
  currentSChannel,
  membersArray,
  editMsg,
  setEditMsg,
  replyTo,
  setReplyTo,
  selectedReaction,
  setselectedReaction,
  noMorePages,
  paginationLoader,
  page,
  setPage,
  firstMsg,
  lastMsg,
  msgRef,
  inputRef,
  addCreateTask,
  deletMessage,
  removeReaction,
}) => {
  // Tracks the most recently rendered date so we know when to drop a divider.
  // Reset on every render — list order is stable per render pass.
  let lastDateKey = null;

  const renderDateDivider = (date) => {
    const m = moment(date);
    if (!m.isValid()) return null;
    const key = m.format("YYYY-MM-DD");
    if (key === lastDateKey) return null;
    lastDateKey = key;
    return (
      <div className="comm-message__date-divider" aria-hidden="true">
        <span className="comm-message__date-pill">{getDateLabel(date)}</span>
      </div>
    );
  };

  return (
    <div className="comm-chatbox__body">
      <div className="comm-message__pagination" ref={firstMsg}>
        {!noMorePages ? (
          <Button
            variant="primary"
            loading={paginationLoader}
            onClick={() => setPage(page + 1)}
          >
            Load More
          </Button>
        ) : (
          "No more Conversations found"
        )}
      </div>

      {conversations.map((msg, index) => {
        let Mentioned_To =
          msg.Mentioned_To &&
          msg.Mentioned_To.split(",").map((id) => Number(id));
        let mensioned = membersArray.filter(
          (member) =>
            Mentioned_To && Mentioned_To.includes(member.User_Id)
        );
        // Reply target may live in `conversations` (server-loaded history) or
        // in `newMessages` (just-sent in this session). Probe both so a reply
        // to a recent in-session message resolves correctly.
        let repliedTo =
          msg.ReplyTo != null
            ? conversations.find(
                (reply) => reply.Team_Message_Id == msg.ReplyTo
              ) ||
              newMessages.find(
                (reply) =>
                  reply.Team_Message_Id == msg.ReplyTo ||
                  reply.message_id == msg.ReplyTo
              )
            : null;
        let sernderName = msg.FirstName + " " + msg.LastName;
        const isSent = msg && msg.Sender_Id == userData.EmpId;
        const dateDivider = renderDateDivider(msg.CreatedOn);
        const isEditing =
          msg.Team_Message_Id == editMsg.Team_Message_Id;
        // Grouped = same sender as prior message, within a 5-minute window
        // on the same calendar day. A new date divider always breaks the group.
        const prevMsg = index > 0 ? conversations[index - 1] : null;
        const isGrouped =
          !!prevMsg &&
          !dateDivider &&
          prevMsg.Sender_Id == msg.Sender_Id &&
          Math.abs(
            moment(msg.CreatedOn).diff(moment(prevMsg.CreatedOn), "minutes")
          ) <= 5;

        return (
          <React.Fragment key={"conversation" + index}>
            {dateDivider}
            <div
              className={
                "comm-message " +
                (isSent ? "comm-message--sent" : "comm-message--received") +
                (isGrouped ? " comm-message--grouped" : "")
              }
              id={"msg" + msg.Team_Message_Id}
              ref={(elem) => (msgRef.current[index] = elem)}
            >
              {!isSent && (
                <div className="comm-message__avatar">
                  <ChatImage
                    name={msg?.FirstName}
                    lastName={msg?.LastName}
                    PhotoPath={
                      ApiConfig.BASE_URL_FOR_IMAGES + msg.PhotoPath
                    }
                  />
                </div>
              )}

              <div className="comm-message__column">
                {!isSent && (
                  <span className="comm-message__sender">{sernderName}</span>
                )}

                {msg.ReplyTo !== null && (
                  <div className="comm-message__reply-context">
                    <RepliesTo
                      sernderName={sernderName}
                      msg={msg}
                      repliedTo={repliedTo}
                      msgRef={msgRef}
                      users={mensioned}
                      index={index}
                    />
                  </div>
                )}

                {msg.Resource_Path != null &&
                  msg.Resource_Path.length > 0 && (
                    <div className="comm-message__attachments">
                      {/* Video check runs first because the document branch
                          uses bare `includes("doc")`/`("xls")` which falsely
                          match any URL containing those substrings (bucket
                          names, CDN paths, etc). Anchoring on the file
                          extension keeps a real `.mp4` from being routed to
                          the file-icon branch. */}
                      {/\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i.test(
                        msg.Resource_Path
                      ) ? (
                        <div className="comm-message__attachment comm-message__attachment--video">
                          <video
                            src={msg.Resource_Path}
                            controls
                            preload="metadata"
                            playsInline
                            className="comm-message__video"
                          />
                        </div>
                      ) : msg.Resource_Path.includes("pdf") ||
                        msg.Resource_Path.includes("xls") ||
                        msg.Resource_Path.includes("xlsx") ||
                        msg.Resource_Path.includes("csv") ||
                        msg.Resource_Path.includes("doc") ||
                        msg.Resource_Path.includes("docx") ||
                        msg.Resource_Path.includes("zip") ? (
                        (() => {
                          const meta = getFileMeta(msg.Resource_Path);
                          return (
                            <a
                              href={msg.Resource_Path}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="comm-message__attachment comm-message__attachment--file"
                            >
                              <span
                                className="comm-message__file-icon"
                                data-ext={meta.ext}
                              >
                                <img src={fileIcon} alt="" aria-hidden="true" />
                              </span>
                              <span className="comm-message__file-info">
                                <span
                                  className="comm-message__file-name"
                                  title={meta.name}
                                >
                                  {meta.name}
                                </span>
                                <span className="comm-message__file-detail">
                                  {meta.ext}
                                </span>
                              </span>
                              <span
                                className="comm-message__file-download"
                                aria-hidden="true"
                              >
                                <img src={downloadIcon} alt="" />
                              </span>
                            </a>
                          );
                        })()
                      ) : msg.Resource_Path.includes("mp3") ||
                        msg.Resource_Path.includes("m4a") ? (
                        <a
                          href={msg.Resource_Path}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="comm-message__attachment"
                        >
                          <audio src={msg.Resource_Path} controls></audio>
                        </a>
                      ) : (
                        <a
                          href={msg.Resource_Path}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="comm-message__attachment"
                        >
                          <img src={msg.Resource_Path} alt="" />
                        </a>
                      )}
                    </div>
                  )}

                {isEditing ? (
                  <Textarea
                    className="comm-message__edit-input"
                    value={
                      editMsg.Team_Message_Text || editMsg.message
                    }
                    onChange={(e) =>
                      setEditMsg({
                        ...editMsg,
                        message: e.target.value,
                        Team_Message_Text: e.target.value,
                      })
                    }
                    onKeyUp={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        e.target.value.length
                      ) {
                        if (socket)
                          socket.emit("edit-message", {
                            schannel_id: currentSChannel,
                            message_id:
                              editMsg.message_id ||
                              editMsg.Team_Message_Id,
                            message:
                              editMsg.Team_Message_Text ||
                              editMsg.message,
                          });
                        setEditMsg({
                          message_id: null,
                          Sender_Id: null,
                        });
                      }
                    }}
                  />
                ) : (
                  msg?.Team_Message_Text && (
                    <div
                      className="comm-message__bubble-wrap"
                      onMouseEnter={() => {
                        setselectedReaction(msg.Team_Message_Id);
                      }}
                      onMouseLeave={() => setselectedReaction(null)}
                    >
                      <div
                        className={
                          "comm-message__bubble " +
                          (isSent
                            ? "comm-message__bubble--sent"
                            : "comm-message__bubble--received")
                        }
                      >
                        <div className="comm-message__text">
                          {msg.Team_Message_Text.includes("@") ? (
                            <MensionUsers
                              text={msg.Team_Message_Text}
                              users={mensioned}
                              index={index}
                            />
                          ) : !isSent &&
                            msg.Team_Message_Text.includes("<a") ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: msg.Team_Message_Text,
                              }}
                            />
                          ) : (
                            <ChatMessage msg={msg?.Team_Message_Text} />
                          )}
                        </div>
                      </div>

                      {selectedReaction &&
                        selectedReaction == msg.Team_Message_Id && (
                          <div className="comm-message__reaction-picker">
                            <ReactionBarSelector
                              iconSize={16}
                              reactions={emojiReactions}
                              onSelect={(selectedEmoji) => {
                                const selectedReaction = emojiReactions.find(
                                  (reaction) => reaction.label === selectedEmoji
                                );
                                const isSelectedEmojiPresent =
                                  msg?.reactions &&
                                  msg.reactions.length &&
                                  msg.reactions.some(
                                    (item) =>
                                      item.reaction == selectedReaction?.id
                                  );
                                if (!isSelectedEmojiPresent) {
                                  socket &&
                                    socket.emit("add-reaction", {
                                      reaction: selectedReaction?.id,
                                      schannel_id: msg?.Team_Channel_Id,
                                      message_id: msg?.Team_Message_Id,
                                    });
                                }
                              }}
                            />
                          </div>
                        )}
                    </div>
                  )
                )}

                <div className="comm-message__meta">
                  {isSent && (
                    <span className="comm-message__receipt">
                      <img
                        src={msg.IsAllRead ? double_tick : gray_tick}
                        alt="double_tick"
                      />
                    </span>
                  )}
                  <span className="comm-message__time">
                    {moment(msg.CreatedOn).fromNow()}
                  </span>
                  {msg.IsEdit && (
                    <span className="comm-message__edited">Edited</span>
                  )}
               {msg?.reactions &&
  msg.reactions.length &&
  msg.reactions.some((item) => {
    const displayEmoji = emojiReactions.find(
      (emj) => emj?.id == item?.reaction
    );
    return displayEmoji?.emoji;
  }) && (
    <span className="comm-message__reactions">
      {msg.reactions.map((item) => {
        let displayEmoji = emojiReactions.find(
          (emj) => emj?.id == item?.reaction
        );

        return displayEmoji?.emoji ? (
          <span
            key={item?.reaction_id}
            className="comm-message__reaction"
            onClick={() =>
              removeReaction(
                item?.reaction_id,
                msg?.Team_Channel_Id,
                msg?.Team_Message_Id
              )
            }
          >
            {displayEmoji.emoji}
          </span>
        ) : null;
      })}
    </span>
)}
                </div>
              </div>

              <Dropdown className="comm-message__actions ActionDropdown">
                <Dropdown.Toggle
                  variant="success"
                  id={"dropdown-basic" + index}
                  className="ActionToggle"
                >
                  <img src={hr_three_dot} alt="" />
                </Dropdown.Toggle>
                <Dropdown.Menu className="ActionMenu">
                  <ul className="actionnmenulist">
                    <li
                      onClick={() => {
                        setReplyTo({ ...msg });
                        inputRef.current.focus();
                      }}
                    >
                      Reply
                    </li>
                    {isSent &&
                      moment(msg.CreatedOn).fromNow() &&
                      (moment(msg.CreatedOn)
                        .fromNow()
                        .includes("seconds") ||
                        (moment(msg.CreatedOn)
                          .fromNow()
                          .includes("minutes") &&
                          Number(
                            moment(msg.CreatedOn)
                              .fromNow()
                              .split(" ")[0]
                          ) <= 15)) && (
                        <li
                          onClick={() => {
                            setEditMsg(msg);
                          }}
                        >
                          Edit
                        </li>
                      )}
                    {isSent && (
                      <li
                        onClick={() => {
                          deletMessage(msg.Team_Message_Id);
                        }}
                      >
                        Delete
                      </li>
                    )}
                    {msg?.Team_Message_Text && (
                      <li
                        onClick={() => {
                          addCreateTask(
                            msg?.Team_Message_Text,
                            isSent
                              ? undefined
                              : msg?.Mentioned_To?.split(",")[0] || ""
                          );
                        }}
                      >
                        Create Task
                      </li>
                    )}
                  </ul>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </React.Fragment>
        );
      })}

      {newMessages &&
        newMessages.length > 0 &&
        newMessages.map((msg, index) => {
          let Mentioned_To =
            msg.Mentioned_To &&
            msg.Mentioned_To.split(",").map((id) => Number(id));
          let mensioned = membersArray.filter(
            (member) =>
              Mentioned_To && Mentioned_To.includes(member.User_Id)
          );
          // Match the reply target against both arrays. Previous fallback
          // compared `filtMsg.message_id == msg.message_id` — that resolved
          // to the message itself, causing nested in-session replies to
          // render their own text as the quote (duplicate above/below).
          let replied =
            msg.ReplyTo != null
              ? conversations.find(
                  (reply) => reply.Team_Message_Id == msg.ReplyTo
                ) ||
                newMessages.find(
                  (filtMsg) =>
                    filtMsg.Team_Message_Id == msg.ReplyTo ||
                    filtMsg.message_id == msg.ReplyTo
                )
              : null;
          let sender_name = msg.sender_name;
          const isSent = msg && msg.Sender_Id == userData.EmpId;
          const dateDivider = renderDateDivider(msg.created_at);
          const isEditing =
            msg.Sender_Id == editMsg.Sender_Id &&
            msg.message_id == editMsg.message_id;
          // Same grouping rule as conversations.map. For the first newMessages
          // item, fall back to the tail of conversations so a sender's last
          // saved message and first new message group across the boundary.
          const prevMsg =
            index > 0
              ? newMessages[index - 1]
              : conversations.length > 0
              ? conversations[conversations.length - 1]
              : null;
          const prevDate = prevMsg
            ? prevMsg.created_at || prevMsg.CreatedOn
            : null;
          const isGrouped =
            !!prevMsg &&
            !dateDivider &&
            prevMsg.Sender_Id == msg.Sender_Id &&
            Math.abs(
              moment(msg.created_at).diff(moment(prevDate), "minutes")
            ) <= 5;

          return (
            <React.Fragment key={"new Message" + index}>
              {dateDivider}
              <div
                className={
                  "comm-message " +
                  (isSent ? "comm-message--sent" : "comm-message--received") +
                  (isGrouped ? " comm-message--grouped" : "")
                }
                id={"msg" + msg.message_id}
                ref={(elem) => (msgRef.current[index] = elem)}
                onBlur={() => setEditMsg({})}
              >
                {!isSent && (
                  <div className="comm-message__avatar">
                    <ChatImage
                      name={msg?.FirstName}
                      lastName={msg?.LastName}
                      PhotoPath={
                        ApiConfig.BASE_URL_FOR_IMAGES + msg.PhotoPath
                      }
                    />
                  </div>
                )}

                <div className="comm-message__column">
                  {!isSent && (
                    <span className="comm-message__sender">
                      {sender_name}
                    </span>
                  )}

                  {msg?.ReplyTo && (
                    <div className="comm-message__reply-context">
                      <RepliesTo
                        sernderName={isSent ? sender_name : msg?.sender_name}
                        msg={msg}
                        repliedTo={replied}
                        msgRef={msgRef}
                      />
                    </div>
                  )}

                  {msg.files && msg.files.length > 0 && (
                    <div className="comm-message__attachments">
                      {msg.files.map((file, fileIndex) => {
                        let path = file.split(".com")[1];
                        const fileExtension =
                          file?.split(".")?.length > 1 &&
                          file
                            ?.split(".")
                            [file?.split(".").length - 1].toLowerCase();
                        const isImage =
                          fileExtension &&
                          (fileExtension.includes("png") ||
                            fileExtension.includes("jpg") ||
                            fileExtension.includes("jpeg") ||
                            fileExtension.includes("gif"));
                        const isAudio =
                          msg.Resource_Path?.includes(".mp3") ||
                          msg.Resource_Path?.includes(".m4a");
                        // Match against the full URL, not just the trailing
                        // `.split(".")` token — presigned S3 URLs frequently
                        // tack a `?signature=...` onto the extension and that
                        // would otherwise turn "mp4" into "mp4?signature=...".
                        const isVideo =
                          typeof file === "string" &&
                          /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i.test(
                            file
                          );

                        if (isSent) {
                          if (isVideo) {
                            return (
                              <div
                                key={"video" + fileIndex}
                                className="comm-message__attachment comm-message__attachment--video"
                              >
                                <video
                                  src={file}
                                  controls
                                  preload="metadata"
                                  playsInline
                                  className="comm-message__video"
                                />
                              </div>
                            );
                          }
                          if (isImage || isAudio) {
                            return (
                              <a
                                key={"image" + fileIndex}
                                href={file}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="comm-message__attachment"
                              >
                                {isAudio ? (
                                  <audio src={file} controls></audio>
                                ) : (
                                  <img src={file} alt="" />
                                )}
                              </a>
                            );
                          }
                          const sentMeta = getFileMeta(file);
                          return (
                            <a
                              key={"image" + fileIndex}
                              href={file}
                              target="_blank"
                              rel="noreferrer"
                              className="comm-message__attachment comm-message__attachment--file"
                            >
                              <span
                                className="comm-message__file-icon"
                                data-ext={sentMeta.ext}
                              >
                                <img src={fileIcon} alt="" aria-hidden="true" />
                              </span>
                              <span className="comm-message__file-info">
                                <span
                                  className="comm-message__file-name"
                                  title={sentMeta.name}
                                >
                                  {sentMeta.name}
                                </span>
                                <span className="comm-message__file-detail">
                                  {sentMeta.ext}
                                </span>
                              </span>
                              <span
                                className="comm-message__file-download"
                                aria-hidden="true"
                              >
                                <img src={downloadIcon} alt="" />
                              </span>
                            </a>
                          );
                        }

                        // Received-side legacy logic uses BASE_URL_CHAT_Image + path.
                        // The original boolean was always false (uses && between
                        // mismatched extensions), so this branch always falls through
                        // to the file icon. Preserving the existing behavior.
                        if (
                          fileExtension == "png" &&
                          fileExtension == "jpg" &&
                          fileExtension == "jpeg" &&
                          fileExtension == "gif" &&
                          fileExtension == "svg"
                        ) {
                          return (
                            <a
                              key={"file send" + fileIndex}
                              href={BASE_URL_CHAT_Image + path}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="comm-message__attachment"
                            >
                              <img
                                src={BASE_URL_CHAT_Image + path}
                                alt=""
                              />
                            </a>
                          );
                        }
                        const recvMeta = getFileMeta(
                          BASE_URL_CHAT_Image + path
                        );
                        return (
                          <a
                            key={"file sen" + fileIndex}
                            href={BASE_URL_CHAT_Image + path}
                            target="_blank"
                            rel="noreferrer"
                            className="comm-message__attachment comm-message__attachment--file"
                          >
                            <span
                              className="comm-message__file-icon"
                              data-ext={recvMeta.ext}
                            >
                              <img src={fileIcon} alt="" aria-hidden="true" />
                            </span>
                            <span className="comm-message__file-info">
                              <span
                                className="comm-message__file-name"
                                title={recvMeta.name}
                              >
                                {recvMeta.name}
                              </span>
                              <span className="comm-message__file-detail">
                                {recvMeta.ext}
                              </span>
                            </span>
                            <span
                              className="comm-message__file-download"
                              aria-hidden="true"
                            >
                              <img src={downloadIcon} alt="" />
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {isEditing ? (
                    <Textarea
                      className="comm-message__edit-input"
                      value={
                        editMsg.Team_Message_Text || editMsg.message
                      }
                      onChange={(e) =>
                        setEditMsg({
                          ...editMsg,
                          message: e.target.value,
                          Team_Message_Text: e.target.value,
                        })
                      }
                      onKeyUp={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          e.target.value.length
                        ) {
                          if (socket)
                            socket.emit("edit-message", {
                              schannel_id: currentSChannel,
                              message_id:
                                editMsg.message_id ||
                                editMsg.Team_Message_Id,
                              message:
                                editMsg.Team_Message_Text ||
                                editMsg.message,
                            });
                          setEditMsg({
                            message_id: null,
                            Sender_Id: null,
                          });
                        }
                      }}
                    />
                  ) : (
                    msg?.message && (
                      <div
                        className="comm-message__bubble-wrap"
                        onMouseEnter={() => {
                          setselectedReaction(msg?.message_id);
                        }}
                        onMouseLeave={() => setselectedReaction(null)}
                      >
                        <div
                          className={
                            "comm-message__bubble " +
                            (isSent
                              ? "comm-message__bubble--sent"
                              : "comm-message__bubble--received")
                          }
                        >
                          <div className="comm-message__text">
                            {msg.message.includes("@") ? (
                              <MensionUsers
                                text={msg.message}
                                users={mensioned}
                                index={index}
                              />
                            ) : !isSent && msg.message.includes("<a") ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: msg.message,
                                }}
                              />
                            ) : (
                              <ChatMessage msg={msg?.message} />
                            )}
                          </div>
                        </div>

                        {selectedReaction &&
                          selectedReaction == msg?.message_id && (
                            <div className="comm-message__reaction-picker">
                              <ReactionBarSelector
                                iconSize={16}
                                reactions={emojiReactions}
                                onSelect={(selectedEmoji) => {
                                  const selectedReaction = emojiReactions.find(
                                    (reaction) =>
                                      reaction.label === selectedEmoji
                                  );
                                  const isSelectedEmojiPresent =
                                    msg?.reactions &&
                                    msg.reactions.length &&
                                    msg.reactions.some(
                                      (item) =>
                                        item.reaction == selectedReaction?.id
                                    );
                                  if (!isSelectedEmojiPresent) {
                                    socket &&
                                      socket.emit("add-reaction", {
                                        reaction: selectedReaction?.id,
                                        schannel_id: msg?.schannel_id,
                                        message_id: msg?.message_id,
                                      });
                                  }
                                }}
                              />
                            </div>
                          )}
                      </div>
                    )
                  )}

                  <div className="comm-message__meta">
                    {isSent && (
                      <span className="comm-message__receipt">
                        <img
                          src={msg.IsAllRead ? double_tick : gray_tick}
                          alt="double_tick"
                        />
                      </span>
                    )}
                    <span className="comm-message__time">
                      {moment(msg.created_at).fromNow()}
                    </span>
                    {msg.IsEdit && (
                      <span className="comm-message__edited">Edited</span>
                    )}
                    {msg?.reactions && msg.reactions.length > 0 && (
                      <span className="comm-message__reactions">
                        {msg.reactions.map((item) => {
                          let displayEmoji = emojiReactions.find(
                            (emj) => emj?.id == item?.reaction
                          );
                          return (
                            <>
                            {displayEmoji?.emoji ? (
                            <span
                              className="comm-message__reaction"
                              onClick={() =>
                                removeReaction(
                                  item?.reaction_id,
                                  msg?.schannel_id,
                                  msg?.message_id
                                )
                              }
                            >
                              {displayEmoji?.emoji && displayEmoji?.emoji}
                            </span>
                            ) : null}
                            </>
                          );
                        })}
                      </span>
                    )}
                  </div>
                </div>

                <Dropdown className="comm-message__actions ActionDropdown">
                  <Dropdown.Toggle
                    variant="success"
                    id={"dropdown-basic" + index}
                    className="ActionToggle"
                  >
                    <img src={hr_three_dot} alt="" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="ActionMenu">
                    <ul className="actionnmenulist">
                      <li
                        onClick={() => {
                          setReplyTo({ ...msg });
                          inputRef.current.focus();
                        }}
                      >
                        Reply
                      </li>
                      {isSent && (
                        <li
                          onClick={() => {
                            setEditMsg(msg);
                          }}
                        >
                          Edit
                        </li>
                      )}
                      {isSent && (
                        <li
                          onClick={() => {
                            deletMessage(msg?.message_id);
                          }}
                        >
                          Delete
                        </li>
                      )}
                      {!isSent && <li>Create Task </li>}
                    </ul>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </React.Fragment>
          );
        })}

      <div ref={lastMsg}></div>
    </div>
  );
};

export default MessageList;
